import {type PaneUpdatedAction} from '@packages/systems/panels';
import {createAction, ActionType} from '@packages/utilities/create-action';
import type {
  DesignerAction,
  DesignerActionFromCreator,
} from '@packages/systems/designer/flux';
import type {
  Locale,
  LocalizationApiShape,
  SettingsPath,
  LocalizedNodes,
  TranslatableNode,
  LocalizationPanelContextualAction,
  CorruptionReport,
  NodeCorruptionType,
} from '@packages/systems/localization-types';
import type {RecordOf, List} from 'immutable';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {LocalizationFetchResponse} from '@packages/domains/localization/dom/data-types';
import type {TranslationExclusionTarget} from '@packages/domains/localization/translation/data-types';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {AutoTranslatedEntry} from '../utils/AutoTranslatedSettingsUtils';

import {
  UNDO,
  REDO,
  RAW_SITE_DATA_RECEIVED,
  EDITOR_PAGE_FETCHED,
  PAGE_SWITCHED,
  LOCALIZATION_BULK_ITEM_DUPLICATING_COMPLETED_REMOTELY,
  AUTO_TRANSLATE_ON_PUBLISH_FINISHED_REMOTELY,
  AUTO_TRANSLATE_ON_OPEN_FINISHED_REMOTELY,
  PAGE_AUTO_TRANSLATED_METADATA_SAVED_REMOTELY,
  COMPONENT_INSTANCE_UNLINKED,
  SYMBOL_ROOT_DUPLICATED,
  ROOT_SYMBOL_NODE_ADDED,
  DATA_TYPE_CHANGED,
  LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED,
  LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED,
  SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND,
  NODE_TEXT_EDITING_ENDED,
} from '@packages/systems/designer/actions';

import type {
  CreateComponentAcrossLocalesStartedAction,
  CreateComponentAcrossLocalesProgressedAction,
  CreateComponentAcrossLocalesFailedAction,
} from './createComponent';

import type {
  DuplicateComponentAcrossLocalesStartedAction,
  DuplicateComponentAcrossLocalesProgressedAction,
  DuplicateComponentAcrossLocalesFailedAction,
} from './duplicateComponent';

import type {
  UnlinkComponentInstanceAcrossLocalesStartedAction,
  UnlinkComponentInstanceAcrossLocalesProgressedAction,
  UnlinkComponentInstanceAcrossLocalesFailedAction,
} from './unlinkComponentInstance';

import type {
  CreateComponentPropertyAcrossLocalesStartedAction,
  CreateComponentPropertyAcrossLocalesProgressedAction,
  CreateComponentPropertyAcrossLocalesFailedAction,
} from './createComponentProperty';

import type {
  LocalizationCorruptionRecoveryStartedAction,
  LocalizationCorruptionRecoveryProgressedAction,
  LocalizationCorruptionRecoveryFailedAction,
  LocalizationCorruptionRecoveryFinishedAction,
  LocalizationCorruptionRecoveryEnabledRemotelyAction,
  LocalizationCorruptionReportUpdatedRemotelyAction,
  CorruptionRecoveryPanelOpenedAction,
  CorruptionRecoveryPanelClosedAction,
  LostTranslationsFetchFinishedAction,
  LostTranslationsFetchFailedAction,
  LostTranslationsReportStatusFetchFinishedAction,
  LostTranslationsReportStatusFetchFailedAction,
} from './corruptionRecovery';

export const CURRENT_LOCALE_CHANGE_BEGIN = 'CURRENT_LOCALE_CHANGE_BEGIN';
export const CURRENT_LOCALE_CHANGED = 'CURRENT_LOCALE_CHANGED';
export const LOCALIZATION_LOCALES_LOADED = 'LOCALIZATION_LOCALES_LOADED';
export const LOCALIZATION_DATA_UPDATED_REMOTELY =
  'LOCALIZATION_DATA_UPDATED_REMOTELY';
export const MULTIPLAYER_LOCALIZATION_DATA_UPDATED_REMOTELY =
  'MULTIPLAYER_LOCALIZATION_DATA_UPDATED_REMOTELY';
export const MULTIPLAYER_EXTERNALIZED_BINDINGS_FLAG_UPDATED_REMOTELY =
  'MULTIPLAYER_EXTERNALIZED_BINDINGS_FLAG_UPDATED_REMOTELY';
export const LOCALIZATION_SETTINGS_SAVED = 'LOCALIZATION_SETTINGS_SAVED';
export const LOCALIZATION_SETTINGS_SAVING = 'LOCALIZATION_SETTINGS_SAVING';
export const LOCALIZATION_SETTINGS_LOCALE_SELECTED =
  'LOCALIZATION_SETTINGS_LOCALE_SELECTED';
export const LOCALIZATION_SETTINGS_LOCALE_DELETED =
  'LOCALIZATION_SETTINGS_LOCALE_DELETED';
export const LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET =
  'LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET';
export const LOCALIZATION_SETTINGS_CHANGES_DISCARDED =
  'LOCALIZATION_SETTINGS_CHANGES_DISCARDED';
export const LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID =
  'LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID';
export const LOCALIZATION_STAGED_SETTINGS_CHANGED =
  'LOCALIZATION_STAGED_SETTINGS_CHANGED';
export const LOCALIZATION_CONTEXTUAL_ACTION_SET =
  'LOCALIZATION_CONTEXTUAL_ACTION_SET';
export const CURRENT_DOM_LOCALIZATION_UPDATED =
  'CURRENT_DOM_LOCALIZATION_UPDATED';
export const CURRENT_SYMBOLS_LOCALIZATION_UPDATED =
  'CURRENT_SYMBOLS_LOCALIZATION_UPDATED';
export const DOM_LOCALIZATION_NODE_RESET = 'DOM_LOCALIZATION_NODE_RESET';
export const PROP_DEFAULT_LOCALIZATION_RESET =
  'PROP_DEFAULT_LOCALIZATION_RESET';
export const LOCALIZED_NODE_SYNCED_WITH_PRIMARY =
  'LOCALIZED_NODE_SYNCED_WITH_PRIMARY';
export const NODE_TRANSLATION_STARTED = 'NODE_TRANSLATION_STARTED';
export const NODE_TRANSLATION_SUCCEEDED = 'NODE_TRANSLATION_SUCCEEDED';
export const NODE_TRANSLATION_ERRORED = 'NODE_TRANSLATION_ERRORED';
export const PAGE_TRANSLATION_STARTED = 'PAGE_TRANSLATION_STARTED';
export const FIELD_PLAINTEXT_TRANSLATION_STARTED =
  'FIELD_PLAINTEXT_TRANSLATION_STARTED';
export const FIELD_PLAINTEXT_TRANSLATION_SUCCEEDED =
  'FIELD_PLAINTEXT_TRANSLATION_SUCCEEDED';
export const FIELD_PLAINTEXT_TRANSLATION_ERRORED =
  'FIELD_PLAINTEXT_TRANSLATION_ERRORED';
export const FIELD_RICHTEXT_TRANSLATION_STARTED =
  'FIELD_RICHTEXT_TRANSLATION_STARTED';
export const FIELD_RICHTEXT_TRANSLATION_SUCCEEDED =
  'FIELD_RICHTEXT_TRANSLATION_SUCCEEDED';
export const FIELD_RICHTEXT_TRANSLATION_ERRORED =
  'FIELD_RICHTEXT_TRANSLATION_ERRORED';
export const LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY =
  'LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY';
export const ALT_TEXT_TRANSLATION_STARTED = 'ALT_TEXT_TRANSLATION_STARTED';
export const ALT_TEXT_TRANSLATION_SUCCEEDED = 'ALT_TEXT_TRANSLATION_SUCCEEDED';
export const ALT_TEXT_TRANSLATION_ERRORED = 'ALT_TEXT_TRANSLATION_ERRORED';
export const CMS_ITEM_TRANSLATION_STARTED = 'CMS_ITEM_TRANSLATION_STARTED';
export const CMS_ITEM_TRANSLATION_SUCCEEDED = 'CMS_ITEM_TRANSLATION_SUCCEEDED';
export const CMS_ITEM_TRANSLATION_ERRORED = 'CMS_ITEM_TRANSLATION_ERRORED';

export const LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED =
  'LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED';

type CurrentLocaleChangeBeginAction = DesignerActionFromCreator<
  typeof currentLocaleChangeBegin
>;
type CurrentLocaleChangedAction = ActionType<
  typeof UNSAFE_currentLocaleChanged
>;

export type LocalizationSettingsSavedAction = DesignerActionFromCreator<
  typeof localizationSettingsSaved
>;

type LocalizationStorageLayerMigrationCompletedAction =
  DesignerActionFromCreator<typeof localizationStorageLayerMigrationCompleted>;

export const localizationStorageLayerMigrationCompleted = createAction<
  typeof LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY,
  LocalizationApiShape
>(LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY);

type LocalizationSettingsChangesDiscardedAction = ActionType<
  typeof localizationSettingsChangesDiscarded
>;

type LocalizationSettingsHreflangPrefixInvalidAction = ActionType<
  typeof localizationSettingsHreflangPrefixInvalid
>;

type LocalizationStagedSettingChangedAction = ActionType<
  typeof localizationStagedSettingsChanged
>;

type LocalizationSettingsPreStagedLocalesResetAction =
  DesignerActionFromCreator<typeof localizationSettingsPreStagedLocalesReset>;

type LocalizationSettingsLocaleSelectedAction = DesignerActionFromCreator<
  typeof localizationSettingsLocaleSelected
>;

type LocalizationSettingsLocaleDeletedAction = DesignerActionFromCreator<
  typeof localizationSettingsLocaleDeleted
>;

type LocalizationSettingsSavingAction = DesignerActionFromCreator<
  typeof localizationSettingsSaving
>;

export const currentLocaleChangeBegin = createAction<
  typeof CURRENT_LOCALE_CHANGE_BEGIN
>(CURRENT_LOCALE_CHANGE_BEGIN);

/**
 * This is marked as unsafe because this just changes the current locale in _LocalizationStore_, and it doesn't
 * force a domsync which helps us guarantee that the content on the Canvas is in sync with the current locale.
 *
 * In almost all cases, you should use `PageHelper.switchToLocalizedPage` instead, since that has the logic to
 * force a domsync before changing the current locale.
 *
 * If you feel like ☝ is not correct for your situation, please reach out to the Localization team.
 */
export const UNSAFE_currentLocaleChanged = createAction<
  typeof CURRENT_LOCALE_CHANGED,
  {
    localeId: Locale['_id'];
    skipApolloCacheReset?: boolean;
  }
>(CURRENT_LOCALE_CHANGED);

export const localizationSettingsSaved = createAction<
  typeof LOCALIZATION_SETTINGS_SAVED,
  LocalizationApiShape
>(LOCALIZATION_SETTINGS_SAVED);

export const localizationSettingsSaving = createAction<
  typeof LOCALIZATION_SETTINGS_SAVING,
  boolean
>(LOCALIZATION_SETTINGS_SAVING);

export const localizationSettingsChangesDiscarded = createAction<
  typeof LOCALIZATION_SETTINGS_CHANGES_DISCARDED
>(LOCALIZATION_SETTINGS_CHANGES_DISCARDED);

export const localizationSettingsHreflangPrefixInvalid = createAction<
  typeof LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID
>(LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID);

export const localizationStagedSettingsChanged = createAction<
  typeof LOCALIZATION_STAGED_SETTINGS_CHANGED,
  {
    keyPath: SettingsPath;
    value: boolean | string | RecordOf<Locale>;
  }
>(LOCALIZATION_STAGED_SETTINGS_CHANGED);

export const localizationSettingsLocaleDeleted = createAction<
  typeof LOCALIZATION_SETTINGS_LOCALE_DELETED
>(LOCALIZATION_SETTINGS_LOCALE_DELETED);

export const localizationSettingsPreStagedLocalesReset = createAction<
  typeof LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET
>(LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET);

export const localizationSettingsLocaleSelected = createAction<
  typeof LOCALIZATION_SETTINGS_LOCALE_SELECTED,
  number | undefined
>(LOCALIZATION_SETTINGS_LOCALE_SELECTED);

export const localizationContextualActionSet = createAction<
  typeof LOCALIZATION_CONTEXTUAL_ACTION_SET,
  LocalizationPanelContextualAction | null
>(LOCALIZATION_CONTEXTUAL_ACTION_SET);

type LocalizationContextualActionSetAction = ActionType<
  typeof localizationContextualActionSet
>;

type CurrentDomLocalizationUpdatedAction = ActionType<
  typeof currentLocalizedDomUpdated
>;

type CurrentSymbolsLocalizationUpdatedAction = DesignerActionFromCreator<
  typeof currentLocalizedSymbolsUpdated
>;

export const currentLocalizedDomUpdated = createAction<
  typeof CURRENT_DOM_LOCALIZATION_UPDATED,
  LocalizedNodes
>(CURRENT_DOM_LOCALIZATION_UPDATED);

export const currentLocalizedSymbolsUpdated = createAction<
  typeof CURRENT_SYMBOLS_LOCALIZATION_UPDATED,
  LocalizedNodes
>(CURRENT_SYMBOLS_LOCALIZATION_UPDATED);

type DomLocalizationNodeResetAction = ActionType<
  typeof domLocalizationNodeReset
>;

export const domLocalizationNodeReset = createAction<
  typeof DOM_LOCALIZATION_NODE_RESET,
  {
    selectedElement: ExpressionElement;
    selectedNativeIdPath: Array<NativeId>;
  }
>(DOM_LOCALIZATION_NODE_RESET);

export const propDefaultLocalizationReset = createAction<
  typeof PROP_DEFAULT_LOCALIZATION_RESET,
  {fieldId: string}
>(PROP_DEFAULT_LOCALIZATION_RESET);

type LocalizedNodeSyncedWithPrimaryAction = ActionType<
  typeof localizedNodeSyncedWithPrimary
>;

export const localizedNodeSyncedWithPrimary = createAction<
  typeof LOCALIZED_NODE_SYNCED_WITH_PRIMARY,
  {
    element: ExpressionElement;
    syncType: NodeCorruptionType;
    pageId?: string;
    branchId?: string;
    componentId?: string;
    localeId: string;
    nodeIds: string[];
    overrideId?: string;
  }
>(LOCALIZED_NODE_SYNCED_WITH_PRIMARY);

export type TranslateSelectedNodeAction = DesignerActionFromCreator<
  typeof translateSelectedNode
>;

export const translateSelectedNode = createAction<
  typeof NODE_TRANSLATION_STARTED,
  {
    selectedElement: ExpressionElement;
    selectedNativeIdPath: Array<NativeId>;
  }
>(NODE_TRANSLATION_STARTED);

export type TranslatePageAction = DesignerActionFromCreator<
  typeof translatePageElements
>;

export const translatePageElements = createAction<
  typeof PAGE_TRANSLATION_STARTED
>(PAGE_TRANSLATION_STARTED);

export type TranslateFieldAction = DesignerActionFromCreator<
  typeof translatePlainTextField
>;

export const translatePlainTextField = createAction<
  typeof FIELD_PLAINTEXT_TRANSLATION_STARTED,
  {
    text: string;
    keyPath: List<string | number>;
    // either the cms item id or the page id
    recordId: string;
    contentType: 'cmsItem' | 'pageSetting';
    formId?: string;
    exclusionTarget?: TranslationExclusionTarget;
  }
>(FIELD_PLAINTEXT_TRANSLATION_STARTED);

export type TranslateRichTextFieldAction = DesignerActionFromCreator<
  typeof translateRichTextField
>;

export const translateRichTextField = createAction<
  typeof FIELD_RICHTEXT_TRANSLATION_STARTED,
  {
    nodes: Array<JSONNode>;
    keyPath: List<string | number>;
    // the cms item id
    recordId: string;
    formId?: string;
    exclusionTarget?: TranslationExclusionTarget;
  }
>(FIELD_RICHTEXT_TRANSLATION_STARTED);

export type TranslateAltTextAction = DesignerActionFromCreator<
  typeof translateAltText
>;

export const translateAltText = createAction<
  typeof ALT_TEXT_TRANSLATION_STARTED,
  {
    altText: string;
    assetId: string;
  }
>(ALT_TEXT_TRANSLATION_STARTED);

export type CMSItemTranslatedStartedAction = DesignerActionFromCreator<
  typeof cmsItemTranslatedStarted
>;

type LocalizationCreateLanguageTargetIntentToggledAction = ActionType<
  typeof localizationCreateLanguageTargetIntentToggled
>;

export const cmsItemTranslatedStarted = createAction<
  typeof CMS_ITEM_TRANSLATION_STARTED,
  {
    nodes: TranslatableNode[];
    // the cms item id
    recordId: string;
    collectionId: string;
    formId?: string;
  }
>(CMS_ITEM_TRANSLATION_STARTED);

export const localizationCreateLanguageTargetIntentToggled = createAction<
  typeof LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED,
  {tag: string; checked: boolean}
>(LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED);

export type LocalizationLocalesLoadedAction = DesignerActionFromCreator<
  typeof localizationLocalesLoaded
>;

export const localizationLocalesLoaded = createAction<
  typeof LOCALIZATION_LOCALES_LOADED,
  LocalizationApiShape
>(LOCALIZATION_LOCALES_LOADED);

export type LocalizationDataUpdatedAction = DesignerActionFromCreator<
  typeof localizationDataUpdatedRemotely
>;

export const localizationDataUpdatedRemotely = createAction<
  typeof LOCALIZATION_DATA_UPDATED_REMOTELY,
  {
    primary?: Locale;
    locales: Array<Locale>;
    publishedSiteBrowserDetection?: boolean;
    enableHreflangLinks?: boolean;
    hrefPrefixSameAsCanonicalUrl?: boolean;
    hrefPrefixForHreflangLinks?: string;
  }
>(LOCALIZATION_DATA_UPDATED_REMOTELY);

export type MultiplayerLocalizationDataUpdatedRemotelyAction = ActionType<
  typeof multiplayerLocalizationDataUpdatedRemotely
>;

export const multiplayerLocalizationDataUpdatedRemotely = createAction<
  typeof MULTIPLAYER_LOCALIZATION_DATA_UPDATED_REMOTELY,
  {
    primary?: Locale;
    locales: Array<Locale>;
    publishedSiteBrowserDetection?: boolean;
    enableHreflangLinks?: boolean;
    hrefPrefixSameAsCanonicalUrl?: boolean;
    hrefPrefixForHreflangLinks?: string;
    // Optional for rolling-deploy compatibility: an absent field means
    // "unchanged" to consumers, never "off".
    enableCorruptionRecovery?: boolean;
    enableExternalizedBindings?: boolean;
  }
>(MULTIPLAYER_LOCALIZATION_DATA_UPDATED_REMOTELY);

export type MultiplayerExternalizedBindingsFlagUpdatedRemotelyAction =
  ActionType<typeof multiplayerExternalizedBindingsFlagUpdatedRemotely>;

/**
 * Sent by the monolith to the multiplayer server when the admin
 * externalized-localization-bindings toggle changes, so the running MPS
 * picks up the new value instead of holding the boot-time one until restart.
 */
export const multiplayerExternalizedBindingsFlagUpdatedRemotely = createAction<
  typeof MULTIPLAYER_EXTERNALIZED_BINDINGS_FLAG_UPDATED_REMOTELY,
  {
    enabled: boolean;
  }
>(MULTIPLAYER_EXTERNALIZED_BINDINGS_FLAG_UPDATED_REMOTELY);

export type LocalizationBulkItemDuplicatingCompletedRemotelyAction =
  DesignerActionFromCreator<
    typeof localizationBulkItemDuplicatingCompletedRemotely
  >;

export const localizationBulkItemDuplicatingCompletedRemotely = createAction<
  typeof LOCALIZATION_BULK_ITEM_DUPLICATING_COMPLETED_REMOTELY,
  string
>(LOCALIZATION_BULK_ITEM_DUPLICATING_COMPLETED_REMOTELY);

export type AutoTranslateOnPublishFinishedRemotelyAction =
  DesignerActionFromCreator<typeof autoTranslateOnPublishFinishedRemotely>;

export const autoTranslateOnPublishFinishedRemotely = createAction<
  typeof AUTO_TRANSLATE_ON_PUBLISH_FINISHED_REMOTELY,
  {
    status:
      | 'success'
      | 'failure'
      | 'skipped'
      | 'over-word-limit'
      | 'out-of-credits';
    /** Over the limit, but translation memory still filled in known content. */
    memoryOnly?: boolean;
  }
>(AUTO_TRANSLATE_ON_PUBLISH_FINISHED_REMOTELY);

export type AutoTranslateOnOpenFinishedRemotelyAction =
  DesignerActionFromCreator<typeof autoTranslateOnOpenFinishedRemotely>;

export const autoTranslateOnOpenFinishedRemotely = createAction<
  typeof AUTO_TRANSLATE_ON_OPEN_FINISHED_REMOTELY,
  {
    pageId: string;
    localeId: string;
    status: 'success' | 'failure' | 'skipped' | 'over-word-limit';
    /** Over the limit, but translation memory still filled in known content. */
    memoryOnly?: boolean;
  }
>(AUTO_TRANSLATE_ON_OPEN_FINISHED_REMOTELY);

export type PageAutoTranslatedMetadataSavedRemotelyAction =
  DesignerActionFromCreator<typeof pageAutoTranslatedMetadataSavedRemotely>;

export const pageAutoTranslatedMetadataSavedRemotely = createAction<
  typeof PAGE_AUTO_TRANSLATED_METADATA_SAVED_REMOTELY,
  {
    pageId: string;
    localeId: string;
    entries: Record<string, AutoTranslatedEntry>;
    clearedFields: string[];
  }
>(PAGE_AUTO_TRANSLATED_METADATA_SAVED_REMOTELY);

// Types from other packages
type RawSiteDataReceivedAction = {
  type: typeof RAW_SITE_DATA_RECEIVED;
  siteData: {
    localization: {
      primary?: Locale;
      locales?: Array<Locale>;
      publishedSiteBrowserDetection?: boolean;
      enableHreflangLinks?: boolean;
      hrefPrefixForHreflangLinks?: string;
      hrefPrefixSameAsCanonicalUrl?: boolean;
      enableCorruptionRecovery?: boolean;
      enableExternalizedBindings?: boolean;
    };
    domNodes: Array<JSONNode>;
    localizedDom?: LocalizedNodes;
    symbols: Array<JSONNode>;
    localizedSymbols?: LocalizedNodes;
    localeId?: string;
    localizations?: LocalizationFetchResponse | null;
  };
  state: any;
};

type EditorPageFetchedAction = {
  type: typeof EDITOR_PAGE_FETCHED;
  maybeLocaleSubdirectory?: string;
  pageData: {
    localization?: {
      primary?: Locale;
      locales?: Array<Locale>;
      publishedSiteBrowserDetection?: boolean;
    };
  };
};

export type PageSwitchedAction = {
  type: typeof PAGE_SWITCHED;
  localizedDom: LocalizedNodes;
  localizedSymbols: LocalizedNodes;
  symbols?: Array<JSONNode>;
  jsonNodes: Array<JSONNode>;
  state: any;
  localeId?: string;
  localizations?: LocalizationFetchResponse | null;
};

type UndoAction = DesignerAction<typeof UNDO, void>;
type RedoAction = DesignerAction<typeof REDO, void>;

type ComponentInstanceUnlinkedAction = DesignerAction<
  typeof COMPONENT_INSTANCE_UNLINKED,
  unknown
>;

type RootSymbolNodeAddedAction = DesignerAction<
  typeof ROOT_SYMBOL_NODE_ADDED,
  unknown
>;

type DuplicateComponentAction = DesignerAction<
  typeof SYMBOL_ROOT_DUPLICATED,
  unknown
>;

type DataTypeChangedAction = DesignerAction<typeof DATA_TYPE_CHANGED, unknown>;

type SymbolRootFieldsCreatedAndBoundAction = DesignerAction<
  typeof SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND,
  unknown
>;

export const MULTIPLAYER_DATA_CATCHUP_RESPONDED =
  'MULTIPLAYER_DATA_CATCHUP_RESPONDED';

type MultiplayerDataCatchupRespondedAction = DesignerAction<
  typeof MULTIPLAYER_DATA_CATCHUP_RESPONDED,
  unknown
>;

type localizationCorruptionReportFetchFinishedAction = DesignerAction<
  typeof LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED,
  {
    payload: {
      response: {report: CorruptionReport; corruptionRecoveryEnabled?: boolean};
    };
  }
>;

type localizationCorruptionReportFetchFailedAction = DesignerAction<
  typeof LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED,
  unknown
>;

// Fired when the content editor saves its current subtree.
// The localization store listens to this to persist externalized binding values.
type NodeTextEditingEndedAction = DesignerAction<
  typeof NODE_TEXT_EDITING_ENDED,
  string
>;

export type LocalizationAction =
  | RootSymbolNodeAddedAction
  | CreateComponentAcrossLocalesStartedAction
  | CreateComponentAcrossLocalesProgressedAction
  | CreateComponentAcrossLocalesFailedAction
  | DuplicateComponentAction
  | DuplicateComponentAcrossLocalesStartedAction
  | DuplicateComponentAcrossLocalesProgressedAction
  | DuplicateComponentAcrossLocalesFailedAction
  | ComponentInstanceUnlinkedAction
  | UnlinkComponentInstanceAcrossLocalesStartedAction
  | UnlinkComponentInstanceAcrossLocalesProgressedAction
  | UnlinkComponentInstanceAcrossLocalesFailedAction
  | DataTypeChangedAction
  | SymbolRootFieldsCreatedAndBoundAction
  | CreateComponentPropertyAcrossLocalesStartedAction
  | CreateComponentPropertyAcrossLocalesProgressedAction
  | CreateComponentPropertyAcrossLocalesFailedAction
  | LocalizationCorruptionRecoveryStartedAction
  | LocalizationCorruptionRecoveryProgressedAction
  | LocalizationCorruptionRecoveryFailedAction
  | LocalizationCorruptionRecoveryFinishedAction
  | CurrentLocaleChangeBeginAction
  | CurrentLocaleChangedAction
  | LocalizationSettingsLocaleDeletedAction
  | LocalizationSettingsLocaleSelectedAction
  | LocalizationSettingsPreStagedLocalesResetAction
  | LocalizationSettingsSavedAction
  | LocalizationSettingsSavingAction
  | LocalizationSettingsChangesDiscardedAction
  | LocalizationSettingsHreflangPrefixInvalidAction
  | LocalizationStagedSettingChangedAction
  | LocalizationContextualActionSetAction
  | LocalizationStorageLayerMigrationCompletedAction
  | CurrentDomLocalizationUpdatedAction
  | CurrentSymbolsLocalizationUpdatedAction
  | DomLocalizationNodeResetAction
  | LocalizedNodeSyncedWithPrimaryAction
  | PaneUpdatedAction
  | RawSiteDataReceivedAction
  | LocalizationLocalesLoadedAction
  | LocalizationDataUpdatedAction
  | MultiplayerLocalizationDataUpdatedRemotelyAction
  | EditorPageFetchedAction
  | PageSwitchedAction
  | UndoAction
  | RedoAction
  | localizationCorruptionReportFetchFinishedAction
  | localizationCorruptionReportFetchFailedAction
  | MultiplayerDataCatchupRespondedAction
  | LocalizationCorruptionRecoveryEnabledRemotelyAction
  | NodeTextEditingEndedAction
  | LocalizationCorruptionReportUpdatedRemotelyAction
  | CorruptionRecoveryPanelOpenedAction
  | CorruptionRecoveryPanelClosedAction
  | LostTranslationsFetchFinishedAction
  | LostTranslationsFetchFailedAction
  | LostTranslationsReportStatusFetchFinishedAction
  | LostTranslationsReportStatusFetchFailedAction
  | LocalizationCreateLanguageTargetIntentToggledAction;
